import React, { useContext, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { Bookmarks } from "@bloomberg/bookmarks";
import { ContentType } from "@bloomberg/bookmarks/dist/interfaces";
import {
    Assistance,
    Disclaimer,
    Footnotes,
    Trashline,
} from "@bloomberg/media-ui-article";
import { TerminalTout } from "@bloomberg/media-ui-core";
import { TipKey, TipVariant, useTippy } from "@bloomberg/tippy";
import { useFortress } from "@foundation/fortress";
import { RichText } from "@foundation/rich-text-react-renderer";

import { WEEKEND_NICODES } from "~constants";
import { BlensWithBrokerAd } from "~components/Blens";
import ContactUs from "~components/ContactUs";
import Footer from "~components/Footer";
import { GridFull } from "~components/GridLayout";
import LanguagePreference from "~components/LanguagePreference";
import BasicByline from "~components/Lede/BasicByline";
import MagazineInsert from "~components/MagazineInsert";
import Share from "~components/Share";
import ShareFeedback from "~components/ShareFeedback";
import StoryBody from "~components/StoryBody";
import { TextToSpeech } from "~components/TextToSpeech";
import TipTout from "~components/TipTout";
import TranslationDisclaimer from "~components/TranslationDisclaimer";
import UpNext from "~components/UpNext";
import WeekendRedirectTout from "~components/WeekendRedirectTout";
import options from "~config/featureRichtext";
import { footnotesOptions } from "~config/richtext";
import { getWeekendCollectionId, OPINION_ARTICLE_TYPE } from "~types/types";
import {
    getNumberOfParagraphs,
    hasInlineAudioPlayer,
} from "~utils/blockAndCharCountUtil";
import { CurrentStoryContext } from "~utils/currentStoryContext";
import isEnabled from "~utils/features";
import { getFortressState } from "~utils/getFortressState";
import { TranslatedMessage } from "~utils/i18n/translate";
import isOpinion from "~utils/isOpinion";

import styles from "./styles.module.css";

const IS_BLENS_ENABLED = isEnabled("enableBlens");
const IS_THE_WEEKEND_ENABLED = isEnabled("enableTheWeekend");
export const SURVEY_FEEDBACK_URL =
    "https://bmedia.iad1.qualtrics.com/jfe/form/SV_0xQ0jMsQ7QLRlj0";

const NEXT_PUBLIC_REGISTRATION_URL =
    process.env.NEXT_PUBLIC_REGISTRATION_URL || "";

const AuthorBioList = dynamic(
    () => import("~components/AuthorBioList").then((mod) => mod),
    {
        /* c8 ignore next */
        loading: () => (
            <p>
                <TranslatedMessage id="loading" defaultMessage="Loading..." />
            </p>
        ),
    }
);

function FeatureContent(): JSX.Element {
    const storyBodyRef = useRef<HTMLDivElement | null>(null);
    const fortressContext = useFortress();
    const { isAllowed, isBlocked, isLoading } =
        getFortressState(fortressContext);

    const { story } = useContext(CurrentStoryContext);

    const {
        assistance,
        authors,
        body,
        brand,
        disclaimer,
        footer,
        footnotes,
        language = "en",
        magazineIssue,
        trashline,
        readingUrl,
        readingDuration,
        translationLanguage,
    } = story;
    const numberOfParagraphs = getNumberOfParagraphs(body.content);
    const isOpinionBrand = isOpinion(story);
    const isEditorialArticle =
        story.opinionArticleType === OPINION_ARTICLE_TYPE.EDITORIAL_BOARD;
    const hasWeekendId =
        IS_THE_WEEKEND_ENABLED && getWeekendCollectionId(story);
    const hasWeekendNicodeAndInlineAudioPlayer =
        IS_THE_WEEKEND_ENABLED &&
        story.nicodes?.includes(WEEKEND_NICODES.all) &&
        hasInlineAudioPlayer(body.content);
    const shouldRenderTTS = Boolean(
        !hasWeekendNicodeAndInlineAudioPlayer &&
            isAllowed &&
            readingUrl &&
            readingDuration
    );

    const [saveArticleElement, setSaveArticleElement] =
        useState<Element | null>(null);
    const [listenAudioElement, setListenAudioElement] =
        useState<Element | null>(null);
    const [followAuthorElement, setFollowAuthorElement] =
        useState<Element | null>(null);
    const [giftArticleElement, setGiftArticleElement] =
        useState<Element | null>(null);
    useTippy({ tipKey: TipKey.TIP_ZERO, variant: TipVariant.DIALOG });
    useTippy({
        tipKey: TipKey.SAVE_ARTICLE,
        variant: TipVariant.POPOVER,
        refNode: saveArticleElement,
    });
    useTippy({
        tipKey: TipKey.LISTEN_AUDIO,
        variant: TipVariant.POPOVER,
        refNode: listenAudioElement,
    });
    useTippy({
        tipKey: TipKey.FOLLOW_AUTHOR,
        variant: TipVariant.POPOVER,
        refNode: followAuthorElement,
        additionalSelectorPath: "[data-component=button]",
    });
    useTippy({
        tipKey: TipKey.GIFT_ARTICLE,
        variant: TipVariant.POPOVER,
        refNode: giftArticleElement,
        additionalSelectorPath: "[data-component=outlined-button]",
        fixed: true,
    });

    return (
        <>
            <GridFull>
                <div className="flex justify-center w-full">
                    <div className={styles.content}>
                        <div className={styles.shareBookmarkMobile}>
                            <Share
                                className={styles.share}
                                fixed
                                hideGiftArticleText
                                giftArticleRef={setGiftArticleElement}
                            />
                        </div>
                        <div className={styles.bylineSpeech}>
                            <BasicByline
                                story={story}
                                className={styles.bylineSpeech}
                            />
                            <div className={styles.bookmarkSpeech}>
                                <div ref={setSaveArticleElement}>
                                    <Bookmarks
                                        apiUrl={NEXT_PUBLIC_REGISTRATION_URL}
                                        articleId={story.id}
                                        publishedAt={story.publishedAt}
                                        contentType={ContentType.Article}
                                        className={styles.bookmarkContainer}
                                    />
                                </div>
                                <LanguagePreference
                                    sourceLanguage={language}
                                    targetLanguage={translationLanguage}
                                />
                                {shouldRenderTTS && (
                                    <div ref={setListenAudioElement}>
                                        <TextToSpeech />
                                    </div>
                                )}
                            </div>
                            {magazineIssue && <MagazineInsert story={story} />}
                        </div>
                        {translationLanguage && (
                            <TranslationDisclaimer
                                position="top"
                                sourceLanguage={language}
                                targetLanguage={translationLanguage}
                            />
                        )}
                        <StoryBody
                            body={body}
                            options={options}
                            isAllowed={isAllowed}
                            isLoading={isLoading}
                            isBlocked={isBlocked}
                            storyBodyRef={storyBodyRef}
                            numberOfParagraphs={numberOfParagraphs}
                            className={styles.body}
                        />
                        <div className={styles.footerWrapper}>
                            {isAllowed && (
                                <>
                                    {assistance && (
                                        <Assistance
                                            language={language}
                                            authors={assistance.map(
                                                ({ name }) => name
                                            )}
                                        />
                                    )}
                                    {disclaimer && (
                                        <Disclaimer>{disclaimer}</Disclaimer>
                                    )}
                                    {trashline && (
                                        <Trashline className={styles.trashline}>
                                            {trashline}
                                        </Trashline>
                                    )}
                                    {translationLanguage && (
                                        <TranslationDisclaimer
                                            position="bottom"
                                            sourceLanguage={language}
                                            targetLanguage={translationLanguage}
                                        />
                                    )}
                                    {footer && isOpinion(story) && (
                                        <Footer footer={footer} />
                                    )}
                                    {hasWeekendId && (
                                        <WeekendRedirectTout story={story} />
                                    )}
                                    {authors?.length && (
                                        <div ref={setFollowAuthorElement}>
                                            <AuthorBioList
                                                brand={brand}
                                                editorialBoard={
                                                    story.editorialBoard
                                                }
                                                isEditorialBoard={
                                                    isEditorialArticle
                                                }
                                                authors={authors}
                                            />
                                        </div>
                                    )}
                                    <Footnotes>
                                        <RichText
                                            story={footnotes}
                                            renderers={footnotesOptions}
                                        />
                                    </Footnotes>
                                </>
                            )}
                            <UpNext />
                            {IS_BLENS_ENABLED && isAllowed && (
                                <BlensWithBrokerAd
                                    wrap
                                    className={styles.blens}
                                    quoteIds={story.blensSecurities}
                                    storyType={story.type}
                                />
                            )}
                            <div>
                                <ContactUs />
                                {!isOpinionBrand && <TipTout />}
                                <ShareFeedback
                                    storySlug={story.slug}
                                    feedbackUrl={SURVEY_FEEDBACK_URL}
                                    className={"mb-[22px]"}
                                />
                                <TerminalTout centerAligned />
                            </div>
                        </div>
                    </div>
                </div>
            </GridFull>
        </>
    );
}

export default FeatureContent;
